import { Component, OnInit, inject, input, output } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faInfoCircle, faPen } from '@fortawesome/free-solid-svg-icons';
import { ShippingConfig, ShippingConfigContext } from '../../../_models/shipping-config.interface';
import { SHIPPING_TYPES } from '../../../_models/shipping-config-rules';
import { PickpointMapPickerComponent } from '../../_shared/shipping-config-form/pickpoint-map-picker.component';
import { ShippingTargetDialogState } from './shipping-target-dialog.state';
import { ShippingTypeSelectPageComponent } from './pages/type-select-page.component';
import { ShippingPositionsPageComponent } from './pages/positions-page.component';

/**
 * Geführter Dialog für das Versandziel einer Operation („+ Versandziel" bzw.
 * Klick auf ein Config-Badge in der Versand-Matrix).
 *
 * Seite 1 fragt den Versandtyp über vier Kacheln ab, Seite 2 listet alle
 * Angaben, die dieser Typ braucht — alle parallel bearbeitbar, grün umrandet
 * sobald sie vollständig sind.
 *
 * Versandtermine fragt der Dialog bewusst NICHT ab: beim Anlegen werden sie
 * still aus den Defaults der Auftragsgruppe gesetzt, geändert werden sie über
 * die Datumszelle der Matrix.
 */
@Component({
  standalone: true,
  selector: 'hawk-shipping-target-dialog',
  imports: [
    FontAwesomeModule,
    PickpointMapPickerComponent,
    ShippingTypeSelectPageComponent,
    ShippingPositionsPageComponent,
  ],
  providers: [ShippingTargetDialogState],
  templateUrl: './shipping-target-dialog.component.html',
})
export class ShippingTargetDialogComponent implements OnInit {
  protected state = inject(ShippingTargetDialogState);
  protected iconInfo = faInfoCircle;
  protected iconEdit = faPen;

  readonly operationId = input.required<number>();
  /** Bestehende Config = Bearbeiten, null = Anlegen. */
  readonly config = input<ShippingConfig | null>(null);
  /** Bereits geladener Kontext — der Dialog wartet nie selbst darauf. */
  readonly context = input.required<ShippingConfigContext>();
  readonly ticketStoreId = input<number | undefined>(undefined);
  readonly ticketLat = input<number | undefined>(undefined);
  readonly ticketLng = input<number | undefined>(undefined);
  readonly referenceDate = input<string | undefined>(undefined);
  readonly projectId = input<number | undefined>(undefined);
  readonly operationTechnicianId = input<number | undefined>(undefined);
  readonly operationTechnicianName = input<string | undefined>(undefined);
  readonly stagingArticlesAssigned = input<boolean>(false);
  readonly hasCommitted = input<boolean>(false);
  /** Nur deckende Versandtypen anbieten (für Staging-Artikel). */
  readonly coveringTypesOnly = input<boolean>(false);
  /** Darf das Ziel entfernt werden? False, sobald Sendungen daran hängen. */
  readonly canDelete = input<boolean>(false);

  /** Gespeicherte Config — der Aufrufer lädt daraufhin neu. */
  readonly saved = output<ShippingConfig>();
  /** Versandziel wurde entfernt — der Aufrufer lädt daraufhin neu. */
  readonly deleted = output<void>();
  readonly closed = output<void>();

  ngOnInit(): void {
    this.state.init({
      operationId: this.operationId(),
      config: this.config(),
      context: this.context(),
      ticketStoreId: this.ticketStoreId(),
      ticketLat: this.ticketLat(),
      ticketLng: this.ticketLng(),
      referenceDate: this.referenceDate(),
      projectId: this.projectId(),
      operationTechnicianId: this.operationTechnicianId(),
      operationTechnicianName: this.operationTechnicianName(),
      stagingArticlesAssigned: this.stagingArticlesAssigned(),
      hasCommitted: this.hasCommitted(),
      coveringTypesOnly: this.coveringTypesOnly(),
    });
  }

  /** Klartext-Label des gewählten Versandtyps für die Kopfzeile. */
  protected typeLabel(): string {
    return SHIPPING_TYPES.find((t) => t.value === this.state.selectedType())?.label ?? '';
  }

  protected async onSave(): Promise<void> {
    const config = await this.state.save();
    if (config) this.saved.emit(config);
  }

  protected async onDelete(): Promise<void> {
    if (!confirm('Dieses Versandziel wirklich entfernen?')) return;
    if (await this.state.deleteConfig()) this.deleted.emit();
  }
}
